<template>
  <div class="select" :class="{ 'is-open': open, 'is-searchable': searchable, 'is-disabled': disabled }" :style="containerStyle" @keydown.stop.prevent="onKeydown">
    <div class="select__control" ref="control" @click.stop="onControlClick" :aria-disabled="String(!!disabled)">
      <input
        ref="input"
        class="select__input"
        :placeholder="placeholder"
        :value="displayText"
        :readonly="disabled || !searchable || !open"
        @input="onInput"
        @focus="onFocus"
      />
      <img class="select__chevron-icon" :class="{ open }" src="@/assets/images/markdown/arrow-down.svg" alt="" aria-hidden="true"/>
    </div>

    <teleport to="body">
      <transition name="fade">
        <div v-if="open" class="select__menu select__menu--portal" role="listbox" :style="menuStyle" :id="_menuId">
           <ul class="select__list" ref="list">
            <template v-for="(item, idx) in visibleItems" :key="item.__key">
              <li v-if="item.type === 'group'" class="select__group">{{ item.label }}</li>
              <li
                v-else
                class="select__option"
                :class="{ selected: item.value === modelValue, active: idx === activeIndex }"
                role="option"
                :aria-selected="item.value === modelValue"
                :title="item.title || ''"
                @mouseenter="activeIndex = idx"
                @mousedown.prevent="select(item)"
              >
                {{ item.label }}
              </li>
            </template>
            <li v-if="!visibleItems.length" class="select__empty">{{ $t('common.notFound') }}</li>
          </ul>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
export default {
  name: 'SelectField',
  emits: ['update:modelValue'],
  props: {
    modelValue: [String, Number, null],
    options: {
      type: Array,
      required: true,
    },
    placeholder: String,
    searchable: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    width: { type: [String, Number], default: null },
  },
  data() {
    return {
      open: false,
      query: '',
      activeIndex: -1,
      clickOutsideHandler: null,
      menuStyle: { position: 'absolute', top: '0px', left: '0px', width: '0px', zIndex: 2000 },
      _repositionHandler: null,
    };
  },
  computed: {
    items() {
      const out = [];
      let key = 0;
      const pushOption = (opt) => out.push({ type: 'option', value: opt.value, label: String(opt.label || ''), __key: 'o-' + (key++) });
      const pushGroup = (label) => out.push({ type: 'group', label: String(label || ''), __key: 'g-' + (key++) });

      this.options.forEach((entry) => {
        if (entry && Array.isArray(entry.options)) {
          pushGroup(entry.label);
          entry.options.forEach(pushOption);
        } else if (entry && 'value' in entry) {
          pushOption(entry);
        }
      });
      return out;
    },
    filteredItems() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.items;
      const out = [];
      let groupBuffer = null;
      for (const it of this.items) {
        if (it.type === 'group') {
          groupBuffer = { ...it, __hasVisible: false };
        } else {
          const ok = it.label.toLowerCase().includes(q);
          if (ok) {
            if (groupBuffer && !groupBuffer.__hasVisible) {
              out.push(groupBuffer);
              groupBuffer.__hasVisible = true;
            }
            out.push(it);
          }
        }
      }
      return out;
    },
    visibleItems() {
      return this.filteredItems;
    },
    selectedLabel() {
      const it = this.items.find((i) => i.type === 'option' && i.value === this.modelValue);
      return it ? it.label : '';
    },
    displayText() {
      if (this.open && this.searchable) return this.query;
      return this.selectedLabel || '';
    },
    containerStyle() {
      if (!this.width) return {};
      const w = typeof this.width === 'number' ? `${this.width}px` : this.width;
      return { width: w };
    },
  },
  watch: {
    open(val) {
      if (val) {
        if (this.disabled) { this.open = false; return }
        this.query = this.selectedLabel;
        this.$nextTick(() => {
          try {
            this.$refs.input?.focus();
            this.$refs.input?.select();
          } catch {}
          this.updatePosition();
          this.scrollActiveIntoView();
        });
        this.clickOutsideHandler = (e) => {
          const menu = document.querySelector('#' + this._menuId);
          if (!this.$el.contains(e.target) && (!menu || !menu.contains(e.target))) this.close();
        };
        document.addEventListener('click', this.clickOutsideHandler, true);
        this._repositionHandler = () => this.updatePosition();
        window.addEventListener('scroll', this._repositionHandler, true);
        window.addEventListener('resize', this._repositionHandler, true);
      } else {
        document.removeEventListener('click', this.clickOutsideHandler, true);
        this.clickOutsideHandler = null;
        window.removeEventListener('scroll', this._repositionHandler, true);
        window.removeEventListener('resize', this._repositionHandler, true);
        this._repositionHandler = null;
        this.query = '';
        this.activeIndex = -1;
      }
    }
  },
  created() {
    this._menuId = 'sf-menu-' + Math.random().toString(36).slice(2);
  },
  methods: {
    onControlClick() {
      if (this.disabled) return;
      if (!this.open) this.open = true;
      this.$nextTick(() => { try { this.$refs.input?.focus() } catch {} })
    },
    toggle() {
      if (this.disabled) return;
      this.open = !this.open;
    },
    close() { this.open = false; },
    onFocus() { if (!this.open && !this.disabled) this.open = true; },
    onInput(e) { this.query = e.target.value || ''; this.activeIndex = this.firstOptionIndex(); },
    firstOptionIndex() {
      return this.visibleItems.findIndex(i => i.type === 'option');
    },
    select(item) {
      if (!item || item.type !== 'option') return;
      this.$emit('update:modelValue', item.value);
      this.close();
    },
    onKeydown(e) {
      if (this.disabled) return;
      const items = this.visibleItems;
      const max = items.length - 1;
      const move = (dir) => {
        if (max < 0) return;
        let idx = this.activeIndex;
        do { idx = (idx + dir + items.length) % items.length; } while (items[idx]?.type !== 'option');
        this.activeIndex = idx;
        this.scrollActiveIntoView();
      };
      if (e.key === 'ArrowDown') { if (!this.open) this.open = true; move(1); }
      else if (e.key === 'ArrowUp') { if (!this.open) this.open = true; move(-1); }
      else if (e.key === 'Enter') { const it = items[this.activeIndex]; if (it) this.select(it); }
      else if (e.key === 'Escape') { this.close(); }
    },
    scrollActiveIntoView() {
      this.$nextTick(() => {
        const list = this.$refs.list;
        if (!list) return;
        const li = list.children?.[this.activeIndex];
        if (!li) return;
        const lb = list.getBoundingClientRect();
        const ib = li.getBoundingClientRect();
        if (ib.top < lb.top) list.scrollTop -= (lb.top - ib.top);
        else if (ib.bottom > lb.bottom) list.scrollTop += (ib.bottom - lb.bottom);
      });
    },
    updatePosition() {
      const ctl = this.$refs.control;
      if (!ctl) return;
      const r = ctl.getBoundingClientRect();
      const top = r.bottom + window.scrollY + 6;
      const left = r.left + window.scrollX;
      this.menuStyle = {
        position: 'absolute',
        top: top + 'px',
        left: left + 'px',
        width: r.width + 'px',
        zIndex: 2000,
      };
    },
  }
};
</script>

<style scoped>
.select { position: relative; width: 100%; }
.select__control {
  display: flex; align-items: center; gap: 8px; width: 100%;
  border: 1px solid #E5E7EB; border-radius: 8px; background: #fff;
  padding: 6px 10px; height: 32px; box-sizing: border-box; cursor: text;
}
.select__control:hover { cursor: pointer; }
.select.is-open.is-searchable .select__control:hover { cursor: text; }
.select.is-disabled .select__control { opacity: .6; cursor: not-allowed; }

.select.is-open .select__control { border-color: #C3D0F7; box-shadow: 0 0 0 2px rgba(47,120,238,.2); }
.select__input {
  flex: 1 1 auto; min-width: 0; border: 0; outline: none; background: transparent;
  color: #111827; font-size: 16px; line-height: 20px; font-family: 'Times New Roman', Times, serif;
}
.select__input[readonly] { cursor: pointer; }

.select__input::placeholder { color: #A0AEC0; }
.select__chevron-icon { width: 8px; height: 8px; transition: transform .15s ease; opacity: .9; }
.select__chevron-icon.open { transform: rotate(180deg); }

.select__menu {
  background: #fff; border: 1px solid #E5E7EB; border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,.12);
}
.select__menu--portal { position: absolute; }
.select__list { max-height: 240px; overflow: auto; margin: 0; padding: 8px; list-style: none; }
.select__group {
  color: #A0AEC0; font-size: 12px; font-weight: 600; padding: 8px 10px 6px; text-transform: none;
}
.select__option {
  padding: 10px 12px; border-radius: 8px; cursor: pointer; color: #111827; font-size: 16px; line-height: 20px;
}
.select__option:hover, .select__option.active { background: #EEF2FF; }
.select__option.selected { font-weight: 400; color: #A0A0AA; }
.select__empty { padding: 12px; color: #6B7280; font-size: 12px; }


:deep(.fade-enter-active), :deep(.fade-leave-active) { transition: opacity .12s ease, transform .12s ease; }
:deep(.fade-enter-from), :deep(.fade-leave-to) { opacity: 0; transform: translateY(-2px); }
</style>
